import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Phone, Calendar, DollarSign, CreditCard, 
  HelpCircle, ChevronRight, Users
} from 'lucide-react';
import FareCalculator from '../components/FareCalculator';

const FaresPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      metaTitle: "Tarifs Taxi Montréal 2026 — Prix Réglementés | MTL Taxi",
      metaDesc: "Tarifs de taxi à Montréal fixés par la Commission des transports du Québec. Tarif fixe aéroport YUL, tarif au compteur, supplément nuit. MTL Taxi — 438-600-4440.",
      title: "Tarifs de taxi à Montréal — Prix officiels 2026",
      intro: "MTL Taxi applique exclusivement les tarifs réglementés par la Commission des transports du Québec (CTQ). Pas de tarification dynamique, pas de frais cachés — vous connaissez le prix avant de monter.",
      
      // Base rates section
      baseTitle: "Tarif de base au compteur — Grand Montréal",
      baseIntro: "Le coût d'une course se compose de trois éléments : le prix de départ affiché au compteur, le prix par kilomètre parcouru, et le prix par heure d'attente ou d'immobilisation.",
      baseNote: "Tarifs établis par la CTQ, en vigueur depuis septembre 2022. Les taxes sont incluses.",
      baseHeaders: { element: "Élément", day: "Tarif de jour (5h–23h)", night: "Tarif de nuit (23h–5h)" },
      baseRows: [
        { element: "Prise en charge (départ)", day: "4,10 $", night: "4,70 $" },
        { element: "Par kilomètre", day: "2,05 $ / km", night: "2,35 $ / km" },
        { element: "Attente / trafic lent", day: "46,20 $ / heure", night: "53,40 $ / heure" },
        { element: "Redevance obligatoire", day: "+ 1,05 $", night: "+ 1,05 $" }
      ],

      // Airport fixed rate section
      airportTitle: "Tarif fixe — Aéroport Montréal-Trudeau (YUL) ↔ Centre-ville",
      airportIntro: "Il existe une tarification fixe exclusivement pour les départs du centre-ville de Montréal vers l'aéroport international Montréal-Trudeau (YUL). Ce tarif s'applique dans les deux sens.",
      airportNote: "Le centre-ville est délimité par : Atwater à l'ouest, Papineau à l'est, la montagne au nord, le fleuve au sud.",
      airportHeaders: { route: "Trajet", day: "Tarif de jour (5h–23h)", night: "Tarif de nuit (23h–5h)" },
      airportRows: [
        { route: "Centre-ville ↔ Aéroport YUL", day: "49,45 $", night: "56,70 $" }
      ],

      // Zone estimates section
      zonesTitle: "Estimations par zone vers l'aéroport YUL",
      zonesIntro: "Pour les adresses hors centre-ville, le taximètre s'applique. Voici des estimations selon les principales zones :",
      zonesNote: "Ces estimations sont indicatives. Le prix final dépend du trajet exact, de la circulation et du temps d'attente. Appelez le 438-600-4440 pour un prix confirmé.",
      zonesHeaders: { zone: "Zone de départ", day: "Estimation de jour", night: "Estimation de nuit", distance: "Distance aprox." },
      zonesRows: [
        { zone: "Centre-ville (forfait fixe)", day: "49,45 $", night: "56,70 $", distance: "~20 km" },
        { zone: "Plateau-Mont-Royal", day: "50 – 60 $", night: "58 – 70 $", distance: "~22 km" },
        { zone: "Rosemont / Hochelaga", day: "55 – 65 $", night: "63 – 75 $", distance: "~25 km" },
        { zone: "Outremont / Côte-des-Neiges", day: "45 – 55 $", night: "52 – 63 $", distance: "~18 km" },
        { zone: "Notre-Dame-de-Grâce", day: "40 – 50 $", night: "46 – 58 $", distance: "~15 km" },
        { zone: "Verdun / LaSalle", day: "40 – 50 $", night: "46 – 58 $", distance: "~14 km" },
        { zone: "Saint-Laurent", day: "30 – 40 $", night: "35 – 46 $", distance: "~10 km" },
        { zone: "Laval (centre)", day: "65 – 80 $", night: "75 – 92 $", distance: "~30 km" },
        { zone: "Longueuil / Brossard", day: "70 – 85 $", night: "80 – 98 $", distance: "~32 km" },
        { zone: "Saint-Lambert", day: "75 – 90 $", night: "86 – 103 $", distance: "~35 km" }
      ],

      // Minivan section
      minivanTitle: "Supplément minivan",
      minivanText: "Un supplément de 10 $ s'applique pour les courses en minivan 6 passagers. Idéal pour les groupes et les familles avec bagages.",
      minivanLink: "En savoir plus sur notre minivan",

      // Payment section
      paymentTitle: "Modes de paiement acceptés",
      paymentMethods: [
        "Carte de crédit (Visa, Mastercard, Amex)",
        "Carte de débit Interac",
        "Comptant",
        "Facturation mensuelle pour les comptes corporatifs"
      ],
      corporateLink: "En savoir plus sur les comptes corporatifs",

      // FAQ section
      faqTitle: "Questions fréquentes sur les tarifs",
      faqs: [
        {
          q: "Le prix est-il le même qu'Uber ?",
          a: "Les tarifs de taxi sont fixes et réglementés par le gouvernement du Québec. Contrairement à Uber, il n'y a pas de tarification dynamique — le prix ne monte pas en heure de pointe ou sous la pluie."
        },
        {
          q: "Y a-t-il des frais supplémentaires ?",
          a: "La seule redevance obligatoire est de 1,05 $ par course, fixée par la CTQ. Aucun autre frais caché."
        },
        {
          q: "Comment connaître le prix exact avant ma course ?",
          a: "Appelez le 438-600-4440 — nous vous donnons une estimation précise selon votre adresse."
        }
      ],

      // CTA section
      ctaTitle: "Réservez maintenant",
      ctaText: "Prix clairs, service fiable, disponible 24h/24.",
      callBtn: "Appeler 438-600-4440",
      bookBtn: "Réserver en ligne",
      
      // Calculator section
      calcTitle: "Estimez le prix de votre course",
      calcDesc: "Entrez votre adresse de départ et votre destination pour obtenir une estimation instantanée basée sur les tarifs officiels de la CTQ.",

      // Footer links
      footerHome: "← Accueil",
      footerAirport: "Navette Aéroport YUL",
      footerMinivan: "Minivan 6 passagers",
      footerCorporate: "Comptes corporatifs"
    },
    en: {
      metaTitle: "Montreal Taxi Fares 2026 — Regulated Prices | MTL Taxi",
      metaDesc: "Montreal taxi fares set by the Commission des transports du Québec. Fixed airport YUL rate, meter rate, night surcharge. MTL Taxi — 438-600-4440.",
      title: "Montreal Taxi Fares — Official 2026 Prices",
      intro: "MTL Taxi applies exclusively the rates regulated by the Commission des transports du Québec (CTQ). No dynamic pricing, no hidden fees — you know the price before you get in.",
      
      baseTitle: "Base Meter Rate — Greater Montreal",
      baseIntro: "The cost of a ride consists of three elements: the starting price displayed on the meter, the price per kilometer traveled, and the price per hour of waiting or immobilization.",
      baseNote: "Rates set by the CTQ, in effect since September 2022. Taxes are included.",
      baseHeaders: { element: "Element", day: "Day Rate (5am–11pm)", night: "Night Rate (11pm–5am)" },
      baseRows: [
        { element: "Pickup (start)", day: "$4.10", night: "$4.70" },
        { element: "Per kilometer", day: "$2.05 / km", night: "$2.35 / km" },
        { element: "Waiting / slow traffic", day: "$46.20 / hour", night: "$53.40 / hour" },
        { element: "Mandatory fee", day: "+ $1.05", night: "+ $1.05" }
      ],

      airportTitle: "Fixed Rate — Montreal-Trudeau Airport (YUL) ↔ Downtown",
      airportIntro: "There is a fixed rate exclusively for departures from downtown Montreal to Montreal-Trudeau International Airport (YUL). This rate applies in both directions.",
      airportNote: "Downtown is bounded by: Atwater to the west, Papineau to the east, the mountain to the north, the river to the south.",
      airportHeaders: { route: "Route", day: "Day Rate (5am–11pm)", night: "Night Rate (11pm–5am)" },
      airportRows: [
        { route: "Downtown ↔ YUL Airport", day: "$49.45", night: "$56.70" }
      ],

      zonesTitle: "Zone Estimates to YUL Airport",
      zonesIntro: "For addresses outside downtown, the meter applies. Here are estimates by main zones:",
      zonesNote: "These estimates are indicative. The final price depends on the exact route, traffic and waiting time. Call 438-600-4440 for a confirmed price.",
      zonesHeaders: { zone: "Departure Zone", day: "Day Estimate", night: "Night Estimate", distance: "Approx. Distance" },
      zonesRows: [
        { zone: "Downtown (fixed rate)", day: "$49.45", night: "$56.70", distance: "~20 km" },
        { zone: "Plateau-Mont-Royal", day: "$50 – 60", night: "$58 – 70", distance: "~22 km" },
        { zone: "Rosemont / Hochelaga", day: "$55 – 65", night: "$63 – 75", distance: "~25 km" },
        { zone: "Outremont / Côte-des-Neiges", day: "$45 – 55", night: "$52 – 63", distance: "~18 km" },
        { zone: "Notre-Dame-de-Grâce", day: "$40 – 50", night: "$46 – 58", distance: "~15 km" },
        { zone: "Verdun / LaSalle", day: "$40 – 50", night: "$46 – 58", distance: "~14 km" },
        { zone: "Saint-Laurent", day: "$30 – 40", night: "$35 – 46", distance: "~10 km" },
        { zone: "Laval (center)", day: "$65 – 80", night: "$75 – 92", distance: "~30 km" },
        { zone: "Longueuil / Brossard", day: "$70 – 85", night: "$80 – 98", distance: "~32 km" },
        { zone: "Saint-Lambert", day: "$75 – 90", night: "$86 – 103", distance: "~35 km" }
      ],

      minivanTitle: "Minivan Supplement",
      minivanText: "A $10 supplement applies for rides in 6-passenger minivan. Ideal for groups and families with luggage.",
      minivanLink: "Learn more about our minivan",

      paymentTitle: "Accepted Payment Methods",
      paymentMethods: [
        "Credit card (Visa, Mastercard, Amex)",
        "Interac debit card",
        "Cash",
        "Monthly billing for corporate accounts"
      ],
      corporateLink: "Learn more about corporate accounts",

      faqTitle: "Frequently Asked Questions About Fares",
      faqs: [
        {
          q: "Is the price the same as Uber?",
          a: "Taxi fares are fixed and regulated by the Quebec government. Unlike Uber, there is no dynamic pricing — the price doesn't go up during rush hour or in the rain."
        },
        {
          q: "Are there any additional fees?",
          a: "The only mandatory fee is $1.05 per ride, set by the CTQ. No other hidden fees."
        },
        {
          q: "How can I know the exact price before my ride?",
          a: "Call 438-600-4440 — we'll give you a precise estimate based on your address."
        }
      ],

      ctaTitle: "Book Now",
      ctaText: "Clear prices, reliable service, available 24/7.",
      callBtn: "Call 438-600-4440",
      bookBtn: "Book online",
      
      calcTitle: "Estimate your ride cost",
      calcDesc: "Enter your pickup address and destination to get an instant estimate based on official CTQ rates.",

      footerHome: "← Home",
      footerAirport: "YUL Airport Shuttle",
      footerMinivan: "6-Passenger Minivan",
      footerCorporate: "Corporate Accounts"
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className="min-h-screen pt-20" data-testid="fares-page">
      <SEO 
        title={t.metaTitle}
        description={t.metaDesc}
        canonical={`https://mtl.taxi/${language === 'fr' ? 'fr/tarifs' : 'en/fares'}`}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <DollarSign className="w-4 h-4" />
            CTQ
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight leading-tight">
            {t.title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t.intro}
          </p>
        </div>
      </section>

      {/* Base Rates Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.baseTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            {t.baseIntro}
          </p>

          <Card className="border-0 shadow-lg overflow-hidden mb-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary text-white">
                  <TableHead className="font-bold text-white">{t.baseHeaders.element}</TableHead>
                  <TableHead className="font-bold text-white text-center">{t.baseHeaders.day}</TableHead>
                  <TableHead className="font-bold text-white text-center">{t.baseHeaders.night}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.baseRows.map((row, index) => (
                  <TableRow key={index} className="hover:bg-muted/20">
                    <TableCell className="font-medium">{row.element}</TableCell>
                    <TableCell className="text-center font-bold text-primary">{row.day}</TableCell>
                    <TableCell className="text-center font-bold text-primary">{row.night}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <p className="text-muted-foreground text-sm italic">
            {t.baseNote}
          </p>
        </div>
      </section>

      {/* Airport Fixed Rate Section */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.airportTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            {t.airportIntro}
          </p>

          <Card className="border-0 shadow-lg overflow-hidden mb-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary text-white">
                  <TableHead className="font-bold text-white">{t.airportHeaders.route}</TableHead>
                  <TableHead className="font-bold text-white text-center">{t.airportHeaders.day}</TableHead>
                  <TableHead className="font-bold text-white text-center">{t.airportHeaders.night}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.airportRows.map((row, index) => (
                  <TableRow key={index} className="hover:bg-muted/20">
                    <TableCell className="font-medium">{row.route}</TableCell>
                    <TableCell className="text-center font-bold text-primary text-lg">{row.day}</TableCell>
                    <TableCell className="text-center font-bold text-primary text-lg">{row.night}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="p-4 bg-blue-50 border-l-4 border-primary rounded-r-lg">
            <p className="text-foreground">{t.airportNote}</p>
          </div>
        </div>
      </section>

      {/* Zone Estimates Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.zonesTitle}
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            {t.zonesIntro}
          </p>

          <Card className="border-0 shadow-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary text-white">
                    <TableHead className="font-bold text-white">{t.zonesHeaders.zone}</TableHead>
                    <TableHead className="font-bold text-white text-center">{t.zonesHeaders.day}</TableHead>
                    <TableHead className="font-bold text-white text-center">{t.zonesHeaders.night}</TableHead>
                    <TableHead className="font-bold text-white text-center">{t.zonesHeaders.distance}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {t.zonesRows.map((row, index) => (
                    <TableRow key={index} className={`hover:bg-muted/20 ${index === 0 ? 'bg-amber-50' : ''}`}>
                      <TableCell className={`font-medium ${index === 0 ? 'font-bold' : ''}`}>{row.zone}</TableCell>
                      <TableCell className={`text-center font-bold text-primary ${index === 0 ? 'text-lg' : ''}`}>{row.day}</TableCell>
                      <TableCell className={`text-center font-bold text-primary ${index === 0 ? 'text-lg' : ''}`}>{row.night}</TableCell>
                      <TableCell className="text-center text-muted-foreground">{row.distance}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
            <p className="text-foreground">{t.zonesNote}</p>
          </div>
        </div>
      </section>

      {/* Fare Calculator Section */}
      <section className="py-16 md:py-20 bg-secondary/30" id="calculateur" data-testid="fare-calculator-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-4">
              {t.calcTitle}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t.calcDesc}
            </p>
          </div>
          <FareCalculator />
        </div>
      </section>

      {/* Minivan Supplement Section */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.minivanTitle}
          </h2>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-muted-foreground text-lg mb-4">{t.minivanText}</p>
                <Link to={getRoute('/services')} className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
                  {t.minivanLink} <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
            {t.paymentTitle}
          </h2>
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <ul className="space-y-3">
                {t.paymentMethods.map((method, index) => (
                  <li key={index} className="flex items-center gap-3 text-foreground text-lg">
                    <CreditCard className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{method}</span>
                    {index === 3 && (
                      <Link to={getRoute('/services')} className="text-primary font-semibold hover:underline ml-2">
                        → {language === 'fr' ? '/corporatif' : '/corporate'}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-8">
            {t.faqTitle}
          </h2>
          <div className="space-y-6">
            {t.faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-foreground text-lg mb-2">{faq.q}</h3>
                      <p className="text-muted-foreground">{faq.a}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-accent-foreground/80 text-lg mb-8">
            {t.ctaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t.callBtn}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-white font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                <Calendar className="w-5 h-5 mr-2" />
                {t.bookBtn}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Links */}
      <section className="py-8 bg-muted/50 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to={getRoute('/')} className="text-muted-foreground hover:text-primary">{t.footerHome}</Link>
            <Link to={getRoute('/airport')} className="text-muted-foreground hover:text-primary">{t.footerAirport}</Link>
            <Link to={getRoute('/services')} className="text-muted-foreground hover:text-primary">{t.footerMinivan}</Link>
            <Link to={getRoute('/services')} className="text-muted-foreground hover:text-primary">{t.footerCorporate}</Link>
          </nav>
        </div>
      </section>
    </div>
  );
};

export default FaresPage;
